import Model from './Model';
import { AxiosInstance, AxiosRequestConfig } from 'axios';
import { JsonApiCollection, JsonApiModel, JsonApiSchema, ModelQuery, NewableModel } from './types';
import pluralize from 'pluralize';
import Collection from './Collection';
import { kebabCase } from 'lodash';

export default class QueryBuilder<T extends Model> {
  static axios: AxiosInstance;
  static forceCacheUpdate: boolean = true;
  private parentModel?: { model: NewableModel<Model>; key?: string | number; relation?: string };
  private includes: string[] = [];
  private filters: {
    [key: string]: string | number | null | undefined | { toString(): string };
  } = {};
  private sort?: string;
  private pagination: { perPage?: number; page?: number } = { perPage: undefined, page: undefined };

  constructor(protected model: NewableModel<T>, protected path?: string) {}

  static getAxios(): AxiosInstance {
    return this.axios;
  }

  getAxios(): AxiosInstance {
    return (this as any).constructor.getAxios();
  }

  static setAxios(axios: AxiosInstance) {
    this.axios = axios;
  }

  async request(config: AxiosRequestConfig): Promise<JsonApiSchema> {
    return (
      await QueryBuilder.getAxios().request({
        method: 'get',
        forceUpdate: QueryBuilder.forceCacheUpdate,
        ...config,
      })
    ).data;
  }

  private buildUrl(components: string[], params?: ModelQuery, suffix?: string) {
    let url = components.join('/');

    if (params) {
      const urlPart: string[] = [];
      Object.entries(params).forEach(([index, value]) => {
        if (value) {
          urlPart.push(`${index}=${value}`);
        }
      });
      if (suffix) {
        url += suffix;
      }
      if (urlPart.length > 0) {
        url += `?${urlPart.join('&')}`;
      }
    }
    return `/v2/${url}`;
  }

  private getBasePathComponents(id?: string | number) {
    const path: string[] = [];
    if (this.path) {
      path.push(this.path);
    } else if (this.parentModel) {
      const model = this.parentModel.model;
      path.push(model.rootUri || pluralize(kebabCase(model.getType())));
      if (this.parentModel.key) {
        path.push(this.parentModel.key.toString());
      }
      if (this.parentModel.relation) {
        path.push(this.parentModel.relation);
      } else {
        path.push(pluralize(kebabCase(this.model.getType())));
      }
      if (id) {
        path.push(id.toString());
      }
    } else {
      path.push(this.model.rootUri || pluralize(kebabCase(this.model.getType())));
      if (id) {
        path.push(id.toString());
      }
    }

    return path;
  }

  private decorateRequest(config: AxiosRequestConfig): AxiosRequestConfig {
    config.params = config.params || {};

    if (this.includes.length > 0) {
      config.params.include = this.includes.join(',').split(',').sort().join(',');
    }

    if (this.sort) {
      config.params.sort = this.sort;
    }
    if (this.filters) {
      Object.keys(this.filters)
        .sort()
        .forEach((key: string) => {
          switch (this.filters[key]) {
            case null:
              config.params[`filter[${key}]`] = 'null';
              break;
            case undefined:
              config.params[`filter[${key}]`] = 'undefined';
              break;
            default:
              config.params[`filter[${key}]`] = this.filters[key];
          }
        });
    }
    if (this.pagination.page && this.pagination.page != 1) {
      config.params.page = this.pagination.page;
    }

    if (this.pagination.perPage) {
      config.params.per_page = this.pagination.perPage;
    }

    return config;
  }

  public createFindRequest(
    id?: string | number,
    params?: ModelQuery,
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    const components = this.getBasePathComponents(id);

    return this.decorateRequest({
      ...(config ? config : {}),
      url: this.buildUrl(components, params),
    });
  }

  async find(id?: string | number, params?: ModelQuery, config?: AxiosRequestConfig): Promise<T> {
    const request = this.createFindRequest(id, params, config);
    return new this.model(((await this.request(request)) as any).data as JsonApiModel);
  }

  public createFetchRequest(params?: ModelQuery, config?: AxiosRequestConfig): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      url: this.buildUrl(this.getBasePathComponents(), params),
    });
  }

  async fetch(params?: ModelQuery, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createFetchRequest(params, config);
    return new (this.model.getCollection())(
      ((await this.request(request)) as any) as JsonApiCollection,
    );
  }

  public createSearchRequest(
    query: string,
    params?: ModelQuery,
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    const pathParts = [
      'search',
      ...this.getBasePathComponents(),
      query,
    ];
    return this.decorateRequest({
      ...(config ? config : {}),
      url: this.buildUrl(pathParts, params),
    });
  }

  async search(
    query: string,
    params?: ModelQuery,
    config?: AxiosRequestConfig,
  ): Promise<Collection<T>> {
    const request = this.createSearchRequest(query, params, config);
    return new (this.model.getCollection())(
      ((await this.request(request)) as any) as JsonApiCollection,
    );
  }

  public createGetRequest(id?: string, config?: AxiosRequestConfig): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'get',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
    });
  }

  async get(id?: string, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createGetRequest(id, config);
    return (await this.request(request)) as any;
  }

  public createDeleteRequest(id?: string, config?: AxiosRequestConfig): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'delete',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
    });
  }

  async delete(id?: string, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createDeleteRequest(id, config);
    return (await this.request(request)) as any;
  }

  public createHeadRequest(id?: string, config?: AxiosRequestConfig): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'head',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
    });
  }

  async head(id?: string, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createHeadRequest(id, config);
    return (await this.request(request)) as any;
  }

  public createOptionsRequest(id?: string, config?: AxiosRequestConfig): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'options',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
    });
  }

  async options(id?: string, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createOptionsRequest(id, config);
    return (await this.request(request)) as any;
  }

  public createPostRequest(
    id?: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'post',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
      data,
    });
  }

  async post(id?: string, data?: any, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createPostRequest(id, data, config);
    return (await this.request(request)) as any;
  }

  public createPutRequest(
    id?: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'put',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
      data,
    });
  }

  async put(id?: string, data?: any, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createPutRequest(id, data, config);
    return (await this.request(request)) as any;
  }

  public createPatchRequest(
    id?: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): AxiosRequestConfig {
    return this.decorateRequest({
      ...(config ? config : {}),
      method: 'patch',
      url: this.buildUrl(this.getBasePathComponents(id), {}),
      data,
    });
  }

  async patch(id?: string, data?: any, config?: AxiosRequestConfig): Promise<Collection<T>> {
    const request = this.createPatchRequest(id, data, config);
    return (await this.request(request)) as any;
  }

  with(relationship: string) {
    this.includes.push(relationship);
    return this;
  }

  where(column: string, value?: string | number | null | { toString(): string }) {
    const filter = /^filter\[.*\]$/.test(column) ? column.slice(7, -1) : column;
    this.filters[filter] = value;
    return this;
  }

  orderBy(column?: string, direction: 'asc' | 'desc' = 'asc') {
    if (!column) {
      this.sort = undefined;
      return this;
    }

    this.sort = `${direction === 'desc' ? '-' : ''}${column}`;
    return this;
  }

  paginate(perPage?: number) {
    this.pagination.perPage = perPage;
    return this;
  }

  page(page?: number) {
    this.pagination.page = page;
    return this;
  }

  from(model: Model | [NewableModel<Model>, string | number | undefined], relation?: string) {
    if (Array.isArray(model)) {
      this.parentModel = {
        model: model[0],
        key: model[1],
        relation,
      };
      return this;
    }
    this.parentModel = {
      model: model.getClass(),
      key: model.getKey(),
      relation,
    };

    return this;
  }

  withQuery(query: ModelQuery) {
    this.paginate(query.per_page || this.pagination.perPage || 10).page(
      query.page || this.pagination.page || 1,
    );

    this.orderBy(query.sort || this.sort, query.direction);

    Object.keys(query).forEach(key => {
      if (['per_page', 'page', 'sort', 'direction'].includes(key)) return;
      // @ts-ignore
      if ([undefined, ''].includes(query[key])) return;
      this.where(key, query[key]);
    });
    return this;
  }
}
